<script setup>
import { ref, computed, watch, onBeforeUnmount } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { frontendURL } from '../../../../helper/URLHelper';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import { useBranding } from 'shared/composables/useBranding';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import IntegrationLogo from 'dashboard/components-next/integrations/IntegrationLogo.vue';

const props = defineProps({
  integrationId: {
    type: [String, Number],
    required: true,
  },
  integrationName: { type: String, default: '' },
  integrationDescription: { type: String, default: '' },
  integrationEnabled: { type: Boolean, default: false },
  integrationAction: { type: String, default: '' },
  actionButtonText: { type: String, default: '' },
  deleteConfirmationText: { type: Object, default: () => ({}) },
});

const { t } = useI18n();
const store = useStore();
const router = useRouter();
const { accountId } = useAccount();
const { replaceInstallationName } = useBranding();

const dialogRef = ref(null);
let contextVersion = 0;
const captureContext = () => {
  const version = contextVersion;
  return () => version === contextVersion;
};

// A API so entrega a integracao quando as credenciais globais dela existem
// (Integrations::App#active?): sem SLACK_CLIENT_SECRET, por exemplo, a lista
// omite o Slack. Quem chega pela URL direta ainda assim renderiza este card, e
// sem id ele saia vazio, com logo quebrado e um botao Conectar que nao leva a
// lugar nenhum. Dizer que falta configurar no servidor e mais honesto do que
// fingir um card.
const indisponivel = computed(() => !props.integrationId);

const openDeletePopup = () => {
  if (dialogRef.value) {
    dialogRef.value.open();
  }
};

const closeDeletePopup = () => {
  if (dialogRef.value) {
    dialogRef.value.close();
  }
};

const deleteIntegration = async () => {
  const isCurrent = captureContext();
  try {
    await store.dispatch('integrations/deleteIntegration', props.integrationId);
    if (!isCurrent()) return;
    useAlert(t('INTEGRATION_SETTINGS.DELETE.API.SUCCESS_MESSAGE'));
  } catch (error) {
    if (!isCurrent()) return;
    useAlert(t('INTEGRATION_SETTINGS.WEBHOOK.DELETE.API.ERROR_MESSAGE'));
  }
};

const confirmDeletion = () => {
  const isCurrent = captureContext();
  closeDeletePopup();
  deleteIntegration();
  if (!isCurrent()) return;
  router.push({ name: 'settings_applications' });
};

watch(
  accountId,
  (next, prev) => {
    if (!next || !prev || String(next) === String(prev)) return;
    contextVersion += 1;
  },
  { flush: 'sync' }
);
onBeforeUnmount(() => {
  contextVersion += 1;
});
</script>

<template>
  <div
    v-if="indisponivel"
    class="flex items-start gap-4 p-6 outline outline-n-container outline-1 bg-n-card rounded-2xl"
  >
    <span
      class="flex-shrink-0 mt-0.5 size-5 i-ri-information-line text-n-slate-11"
    />
    <div>
      <h3 class="mb-1 text-heading-1 text-n-slate-12">
        {{ $t('INTEGRATION_SETTINGS.UNAVAILABLE.TITLE') }}
      </h3>
      <p class="text-n-slate-11 text-body-main">
        {{ $t('INTEGRATION_SETTINGS.UNAVAILABLE.BODY') }}
      </p>
    </div>
  </div>
  <div
    v-else
    class="flex flex-col items-start justify-between lg:flex-row lg:items-center p-6 outline outline-n-container outline-1 bg-n-card rounded-2xl gap-6"
  >
    <div
      class="flex items-start lg:items-center justify-start flex-1 m-0 gap-6 flex-col lg:flex-row"
    >
      <IntegrationLogo
        :integration-id="integrationId"
        :name="integrationName"
        size="lg"
      />
      <div>
        <h3 class="mb-1 text-heading-1 text-n-slate-12">
          {{ integrationName }}
        </h3>
        <p class="text-n-slate-11 text-body-main">
          {{ replaceInstallationName(integrationDescription) }}
        </p>
      </div>
    </div>
    <div class="flex justify-center items-center mb-0">
      <router-link
        :to="
          frontendURL(
            `accounts/${accountId}/settings/integrations/` + integrationId
          )
        "
      >
        <div v-if="integrationEnabled">
          <div v-if="integrationAction === 'disconnect'">
            <Button
              :label="
                actionButtonText ||
                $t('INTEGRATION_SETTINGS.WEBHOOK.DELETE.BUTTON_TEXT')
              "
              faded
              ruby
              @click="openDeletePopup"
            />
          </div>
          <div v-else>
            <Button
              faded
              blue
              :label="t('INTEGRATION_SETTINGS.WEBHOOK.CONFIGURE')"
            />
          </div>
        </div>
      </router-link>
      <div v-if="!integrationEnabled">
        <slot name="action">
          <a :href="integrationAction">
            <Button
              faded
              blue
              :label="t('INTEGRATION_SETTINGS.CONNECT.BUTTON_TEXT')"
            />
          </a>
        </slot>
      </div>
    </div>
    <Dialog
      ref="dialogRef"
      type="alert"
      :title="
        deleteConfirmationText.title ||
        t('INTEGRATION_SETTINGS.WEBHOOK.DELETE.CONFIRM.TITLE')
      "
      :description="
        deleteConfirmationText.message ||
        t('INTEGRATION_SETTINGS.WEBHOOK.DELETE.CONFIRM.MESSAGE')
      "
      :confirm-button-label="
        t('INTEGRATION_SETTINGS.WEBHOOK.DELETE.CONFIRM.YES')
      "
      :cancel-button-label="t('INTEGRATION_SETTINGS.WEBHOOK.DELETE.CONFIRM.NO')"
      @confirm="confirmDeletion"
    />
  </div>
</template>
